import styles from './RenameTaskHeader.module.css'
import {TextInput, Button} from '@primer/react'
import {useState, useRef, useId} from 'react'
import {setTaskTitleOverride} from '@github-ui/copilot-shared-ui/hooks/use-task-title-override'
import {useRenameTask} from '../../hooks/use-rename-task'
import {sendEvent} from '@github-ui/hydro-analytics'

interface RenameTaskHeaderProps {
  taskName: string
  displayTitle: string
  taskId?: string
  repoOwner?: string
  repoName?: string
  onExit: () => void
  onUserRename?: (title: string) => void
  apiUrl?: string
}

export function RenameTaskHeader({
  taskName,
  displayTitle,
  taskId,
  repoOwner,
  repoName,
  onExit,
  onUserRename,
  apiUrl,
}: RenameTaskHeaderProps) {
  const {mutateAsync: renameMutateAsync, isPending: isRenamePending} = useRenameTask({
    owner: repoOwner,
    repo: repoName,
    taskId,
    apiUrl,
  })
  const [renameValue, setRenameValue] = useState(taskName)
  const [renameError, setRenameError] = useState<string | null>(null)
  const renameInputRef = useRef<HTMLInputElement>(null)
  const renameErrorId = useId()

  const exitRenameMode = () => {
    setRenameError(null)
    onExit()
  }

  const handleSaveRename = async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === taskName) {
      exitRenameMode()
      return
    }

    setRenameError(null)
    try {
      await renameMutateAsync({name: trimmed})
      if (taskId) setTaskTitleOverride(taskId, trimmed, taskName)
      onUserRename?.(trimmed)
      sendEvent('agent_sessions.task_actions', {
        target: 'RENAME_TASK_SAVE',
        task_id: taskId,
      })
      exitRenameMode()
    } catch {
      setRenameError('Failed to rename task. Please try again.')
    }
  }

  const handleRenameKeyDown = (event: React.KeyboardEvent) => {
    if (event.nativeEvent.isComposing) return
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (event.key === 'Enter') {
      event.preventDefault()
      handleSaveRename()
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    } else if (event.key === 'Escape') {
      event.preventDefault()
      exitRenameMode()
    }
  }

  return (
    <div className={styles.renameHeader}>
      <h1 className="sr-only">{displayTitle}</h1>
      <TextInput
        ref={renameInputRef}
        value={renameValue}
        onChange={e => {
          setRenameValue(e.target.value)
          setRenameError(null)
        }}
        onKeyDown={handleRenameKeyDown}
        aria-label="Rename task"
        aria-describedby={renameError ? renameErrorId : undefined}
        validationStatus={renameError ? 'error' : undefined}
        className={styles.renameInput}
        autoFocus
      />
      <div className={styles.renameActions}>
        <Button size="small" onClick={exitRenameMode}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="primary"
          onClick={handleSaveRename}
          disabled={!renameValue.trim() || isRenamePending}
        >
          {isRenamePending ? 'Saving…' : 'Save'}
        </Button>
      </div>
      {renameError && (
        <div id={renameErrorId} role="alert" className={styles.renameError}>
          {renameError}
        </div>
      )}
    </div>
  )
}
